import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { formatDisplayDateTime } from '../utils/dateUtils';
import { Clock, Users, Building2, CheckCircle, XCircle, AlertTriangle, Search, Filter, Eye, MessageSquare, File as FileEdit, ArrowRight } from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  phone?: string;
  status: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
  type: string;
  status: string;
}

interface Timesheet {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  site_id: string | null;
  site?: Site | null;
}

interface AmendmentRequest {
  id: string;
  worker_id: string;
  timesheet_id: string;
  amendment_type: 'clock_in' | 'clock_out';
  requested_time: string;
  original_time: string;
  reason: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  admin_notes?: string | null;
  created_at: string;
  updated_at: string;
  worker?: Worker;
  timesheet?: Timesheet;
}

function formatTimeOnly(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Australia/Sydney',
  });
}

export const TimesheetAmendmentRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<AmendmentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AmendmentRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchRequests();

    const sub = supabase
      .channel('ops_amendments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'time_amendments' }, () => {
        fetchRequests();
      })
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, []);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('time_amendments')
        .select(`
          *,
          workers!inner(id, name, role, email, phone, status),
          timesheets!inner(id, clock_in_time, clock_out_time, site_id, sites(id, name, address, type, status))
        `)
        .order('requested_at', { ascending: false });

      if (error) throw error;

      const mapped: AmendmentRequest[] = (data || []).map((row: Record<string, unknown>) => {
        const worker = row.workers as Worker | undefined;
        const timesheetRow = row.timesheets as Record<string, unknown> | undefined;
        const site = timesheetRow?.sites as Site | undefined;
        const timesheet: Timesheet | undefined = timesheetRow
          ? {
              id: timesheetRow.id as string,
              clock_in_time: timesheetRow.clock_in_time as string,
              clock_out_time: (timesheetRow.clock_out_time as string | null) ?? null,
              site_id: (timesheetRow.site_id as string | null) ?? null,
              site: site ?? null,
            }
          : undefined;

        return { ...(row as AmendmentRequest), worker, timesheet };
      });

      setRequests(mapped);
    } catch (error) {
      console.error('Error fetching amendment requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewRequest = async (requestId: string, newStatus: 'approved' | 'denied') => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('time_amendments')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id ?? null,
          admin_notes: reviewNotes || null,
        })
        .eq('id', requestId);

      if (error) throw error;

      if (newStatus === 'approved' && selectedRequest) {
        const fieldToUpdate =
          selectedRequest.amendment_type === 'clock_in' ? 'clock_in_time' : 'clock_out_time';

        const newClockIn = selectedRequest.amendment_type === 'clock_in'
          ? selectedRequest.requested_time
          : selectedRequest.timesheet?.clock_in_time;
        const newClockOut = selectedRequest.amendment_type === 'clock_out'
          ? selectedRequest.requested_time
          : selectedRequest.timesheet?.clock_out_time;

        let recalculatedHours: number | null = null;
        if (newClockIn && newClockOut) {
          const diffMs = new Date(newClockOut).getTime() - new Date(newClockIn).getTime();
          recalculatedHours = Math.max(0, diffMs / (1000 * 60 * 60));
        }

        const { error: tsError } = await supabase
          .from('timesheets')
          .update({
            [fieldToUpdate]: selectedRequest.requested_time,
            total_hours: recalculatedHours,
            is_amended: true,
            amended_at: new Date().toISOString(),
            amended_note: `Approved amendment (${selectedRequest.amendment_type.replace('_', ' ')})`,
          })
          .eq('id', selectedRequest.timesheet_id);

        if (tsError) {
          console.error('Failed to apply amendment to timesheet:', tsError);
          alert('Amendment approved but the timesheet could not be updated automatically. Please update it manually.');
        }
      }

      setShowReviewModal(false);
      setSelectedRequest(null);
      setReviewNotes('');
      fetchRequests();
    } catch (error) {
      console.error('Error reviewing amendment:', error);
      alert('Failed to review amendment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'denied':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'denied':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch =
      request.worker?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.timesheet?.site?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || request.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const deniedCount = requests.filter(r => r.status === 'denied').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Timesheet Amendment Requests</h1>
        <p className="text-sm text-gray-600 mt-1">
          Review and approve worker requests to correct their clock in or clock out times
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileEdit className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-xl font-bold text-gray-900">{requests.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-xl font-bold text-green-600">{approvedCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Denied</p>
              <p className="text-xl font-bold text-red-600">{deniedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by worker name, reason, or site..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRequests.map((request) => (
          <div
            key={request.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            {/* Header with Status */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileEdit className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 capitalize">
                    {request.amendment_type.replace('_', ' ')} Amendment
                  </h3>
                  <p className="text-sm text-gray-500">
                    {formatDisplayDateTime(request.requested_at)}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                <div className="flex items-center space-x-1">
                  {getStatusIcon(request.status)}
                  <span className="capitalize">{request.status}</span>
                </div>
              </div>
            </div>

            {/* Worker Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Worker</span>
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium">{request.worker?.name}</p>
                <p className="text-xs capitalize">{request.worker?.role?.replace('_', ' ')}</p>
                <p className="text-xs">{request.worker?.email}</p>
              </div>
            </div>

            {/* Site Information (if applicable) */}
            {request.timesheet?.site && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Building2 className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-900">Related Site</span>
                </div>
                <div className="text-sm text-orange-800">
                  <p className="font-medium">{request.timesheet.site.name}</p>
                  <p className="text-xs">{request.timesheet.site.address}</p>
                </div>
              </div>
            )}

            {/* Time Change */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-1">Original Time</p>
                  <p className="text-sm font-medium text-gray-800">{formatTimeOnly(request.original_time)}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-1">Requested Time</p>
                  <p className="text-sm font-medium text-orange-700">{formatTimeOnly(request.requested_time)}</p>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded-md p-2 border">
                  {request.reason}
                </p>
              </div>

              {/* Review Information */}
              {request.status !== 'pending' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Review Details</label>
                  <div className="bg-gray-50 rounded-md p-2 border">
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>
                        <span className="font-medium">Reviewed:</span>{' '}
                        {request.reviewed_at ? formatDisplayDateTime(request.reviewed_at) : 'N/A'}
                      </p>
                      {request.admin_notes && (
                        <p>
                          <span className="font-medium">Notes:</span> {request.admin_notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex space-x-2 pt-3 border-t border-gray-200">
              {request.status === 'pending' && (
                <button
                  onClick={() => {
                    setSelectedRequest(request);
                    setReviewNotes('');
                    setShowReviewModal(true);
                  }}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  <Eye className="h-4 w-4" />
                  <span>Review</span>
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedRequest(request);
                  setShowReviewModal(true);
                }}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <FileEdit className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterStatus ? 'No matching requests found' : 'No amendment requests'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterStatus
              ? 'Try adjusting your search or filter criteria.'
              : 'Workers can request corrections to their clock in or clock out times.'}
          </p>
          {(searchTerm || filterStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
              }}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Review Amendment Request</h3>
              </div>
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedRequest(null);
                  setReviewNotes('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Request Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Request Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Worker:</span>
                    <p className="text-gray-900">{selectedRequest.worker?.name}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Role:</span>
                    <p className="text-gray-900 capitalize">{selectedRequest.worker?.role?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Amendment Type:</span>
                    <p className="text-gray-900 capitalize">{selectedRequest.amendment_type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedRequest.status)}`}>
                      {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Time Change */}
                <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-500 mb-1">Original Time</p>
                      <p className="text-sm font-medium text-gray-800">{formatTimeOnly(selectedRequest.original_time)}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-500 mb-1">Requested Time</p>
                      <p className="text-sm font-medium text-orange-700">{formatTimeOnly(selectedRequest.requested_time)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <span className="font-medium text-gray-700">Reason:</span>
                  <p className="text-gray-900 mt-1 bg-white rounded-md p-3 border">
                    {selectedRequest.reason}
                  </p>
                </div>

                {selectedRequest.timesheet?.site && (
                  <div className="mt-4">
                    <span className="font-medium text-gray-700">Related Site:</span>
                    <p className="text-gray-900">{selectedRequest.timesheet.site.name} - {selectedRequest.timesheet.site.address}</p>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              {selectedRequest.status === 'pending' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="inline h-4 w-4 mr-1" />
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Add notes about the decision..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Approving this request will automatically update the worker's timesheet with the requested time.
                  </p>
                </div>
              )}

              {/* Existing Admin Notes */}
              {selectedRequest.admin_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Previous Admin Notes</h4>
                  <p className="text-sm text-blue-800">{selectedRequest.admin_notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                {selectedRequest.status === 'pending' ? (
                  <>
                    <button
                      onClick={() => handleReviewRequest(selectedRequest.id, 'denied')}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {submitting ? 'Processing...' : 'Deny Request'}
                    </button>
                    <button
                      onClick={() => handleReviewRequest(selectedRequest.id, 'approved')}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {submitting ? 'Processing...' : 'Approve Request'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setShowReviewModal(false);
                      setSelectedRequest(null);
                      setReviewNotes('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
